// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@guestsupply/App/Components/Notify";
import xngGaCheckoutStepsEvent from "@guestsupply/App/Handlers/GoogleAnalytics/Checkout/CheckoutStepsEvent";
import addressValidation from "@guestsupply/App/Handlers/XNG/AddressValidation";
import { xCommonLoadingOverlay } from "@guestsupply/App/Handlers/XNG/Common";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import StepperContextProvider, { StepperContext } from "@guestsupply/App/Widgets/Stepper/StepperContext";
import { checkoutSteps, checkoutTitles, CHECKOUT_STEPS, GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import { scrollToFirstFormError } from "@insite/client-framework/Common/CustomerSelectors";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import clearMessages from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ClearMessages";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import setInitialValues from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetInitialValues";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetIsPreloadingData";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import { CartModel } from "@insite/client-framework/Types/ApiModels";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { Component, createContext, Dispatch } from "react";
import { connect, ResolveThunks } from "react-redux";
import { AnyAction } from "redux";

const mapDispatchToProps = {
    preloadCheckoutShippingData: makeHandlerChainAwaitable(preloadCheckoutShippingData),
    setIsPreloadingData,
    updateCart: makeHandlerChainAwaitable(updateCart),
    setInitialValues,
    clearMessages,
    addressValidation: makeHandlerChainAwaitable(addressValidation),
    xCommonLoadingOverlay,
    setIsSubmitted:
        ({ isSubmitted }: { isSubmitted: boolean }) =>
        (dispatch: Dispatch<AnyAction>) => {
            dispatch({
                type: "xAddressValidation/SetIsSubmittedAddressValidationData",
                isSubmitted,
            });
        },
    clearAddressValidationData: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xAddressValidation/ClearAddressValidationData",
        });
    },
    loadCurrentCart: makeHandlerChainAwaitable(loadCurrentCart),
    // xng-change: add google event
    xngGaCheckoutStepsEvent,
};

const mapStateToProps = (state: CustomAppState) => {
    const cartId = state.pages.checkoutShipping.cartId;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);

    return {
        cartId,
        isPreloadingData: state.pages.checkoutShipping.isPreloadingData,
        reviewAndSubmitPageLink: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage"),
        location: getLocation(state),
        isUpdatingCustomer: state.pages.checkoutShipping.isUpdatingCustomer,
        isAuth: xIsAuthenticated(state),
        cart: cartState.value,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        disableAddressValidation: getSettingsCollection(state).customSettings?.disableAddressValidation,
        billingAddressFormState: state.pages.checkoutShipping.billingAddressFormState,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isBillingAddressUpdateRequired: state.pages.checkoutShipping.isBillingAddressUpdateRequired,
        // xng-change: add google event
        xGoogleAnalytics: state.xGoogleAnalytics,
    };
};

// XNG-Changes: adding styling for modals
export interface CheckoutShippingPageStyle {
    checkoutShippingPageModal?: ModalPresentationProps;
}

export const checkoutShippingPageStyle: CheckoutShippingPageStyle = {};

const styles = checkoutShippingPageStyle;

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

type Validator = () => Promise<boolean>;

type State = {
    formSubmitAttempted: boolean;
    validators: {
        [key: string]: Validator | undefined;
    };
    isRedirecting: boolean;
    insufficientInventoryAtCheckout: boolean;
    someCartLinesNotValidForWarehouse: boolean;
};

export const CheckoutShippingFormContext = createContext<State>({
    formSubmitAttempted: false,
    validators: {},
    isRedirecting: false,
    insufficientInventoryAtCheckout: false,
    someCartLinesNotValidForWarehouse: false,
});

class CheckoutShippingPage extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            formSubmitAttempted: false,
            validators: {},
            isRedirecting: false,
            insufficientInventoryAtCheckout: false,
            someCartLinesNotValidForWarehouse: false,
        };
    }

    async componentDidMount() {
        if (this.props.isAuth) {
            this.props.xCommonLoadingOverlay({ key: "add", value: "Checkout_Shipping_Page" });
            this.props.loadCurrentCart({
                onComplete: () => {
                    this.props.xCommonLoadingOverlay({ key: "clear", value: "Checkout_Shipping_Page" });
                },
            });
        }
        if (!this.props.isPreloadingData) {
            const { cartId } = parseQueryString<{ cartId?: string }>(this.props.location.search);
            await this.props.preloadCheckoutShippingData({ cartId });
            this.props.setInitialValues();
        }
        this.props.setIsPreloadingData({ isPreloadingData: false });
        this.props.clearMessages();
        this.props.clearAddressValidationData();
        this.props.xngGaCheckoutStepsEvent({
            checkoutEventType: GOOGLE_EVENTS.BEGIN_CHECKOUT,
            includeCheckoutEvent: this.props.xGoogleAnalytics.includeBeginCheckoutEvent,
        });
    }

    handleUpdateCart = async () => {
        let cart = this.props.cart;
        await this.props.updateCart({
            onComplete: (result: any) => {
                if (result?.apiResult?.cart) {
                    cart = result.apiResult?.cart;
                }
            },
        });
        return cart;
    };

    handleUpdateCartCompleted = (cart: CartModel | undefined) => {
        if (!cart) {
            return;
        }
        if (
            cart &&
            !cart.canCheckOut &&
            cart.properties.someCartLinesNotValidForWarehouse === "true" &&
            !cart.cartLines?.some(o => o.cantBuy)
        ) {
            this.setState({
                someCartLinesNotValidForWarehouse: true,
            });
        } else if (cart && !cart.canCheckOut && !cart.cartLines?.some(o => o.cantBuy)) {
            this.setState({
                insufficientInventoryAtCheckout: true,
            });
        } else {
            this.setState({ isRedirecting: true });
            // TODO ISC-12556

            // xng-change: add google event
            this.props.xngGaCheckoutStepsEvent({
                checkoutEventType: GOOGLE_EVENTS.ADD_SHIPPING_INFO,
                includeCheckoutEvent: this.props.xGoogleAnalytics.includeCheckoutShippingInfoEvent,
            });

            const continueUrl = this.props.cartId
                ? `${this.props.reviewAndSubmitPageLink!.url}?cartId=${this.props.cartId}`
                : this.props.reviewAndSubmitPageLink!.url;
            this.props.history.push(continueUrl);
        }
    };

    handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        this.setState({ formSubmitAttempted: true });

        const validators: Validator[] = [];
        Object.keys(this.state.validators).forEach(key => {
            const validator = this.state.validators[key];
            if (validator) {
                validators.push(validator);
            }
        });
        const results = await Promise.all(validators.map(v => v()));
        if (!results.every(o => o)) {
            scrollToFirstFormError();
            return;
        }

        // XNG-CHANGES
        if (
            !this.props.disableAddressValidation &&
            (this.props.currentUserIsGuest || this.props.isBillingAddressUpdateRequired)
        ) {
            const address = this.props.billingAddressFormState?.address;
            if (!address) {
                return;
            }
            this.props.xCommonLoadingOverlay({ key: "add", value: "Checkout_Shipping_Page" });
            const { address1, address2, city, country, postalCode, phone, state } = address;
            this.props.addressValidation({
                address1,
                address2,
                city,
                stateProvince: state?.abbreviation || "",
                country: country?.abbreviation || "",
                phone,
                postalCode,
                contact: "",
                onComplete: () => {
                    this.props.xCommonLoadingOverlay({ key: "clear", value: "Checkout_Shipping_Page" });
                    this.props.setIsSubmitted({ isSubmitted: true });
                },
            });
        } else {
            const updatedCart = await this.handleUpdateCart();
            this.handleUpdateCartCompleted(updatedCart);
        }
    };
    modalCloseHandler = () => {
        if (this.props.cartPageUrl) {
            this.props.history.push(this.props.cartPageUrl);
        }
        this.setState({
            someCartLinesNotValidForWarehouse: false,
            insufficientInventoryAtCheckout: false,
        });
    };
    render() {
        const titleKey = this.props.isAuth ? "auth" : "unAuth";
        checkoutSteps[0] = {
            ...checkoutSteps[0],
            name: checkoutTitles[CHECKOUT_STEPS.Addresses][titleKey],
            displayName: checkoutTitles[CHECKOUT_STEPS.Addresses][titleKey],
        };
        return (
            <StepperContextProvider initialStep={1} allowStepClick={false} steps={checkoutSteps}>
                <Page data-test-selector="checkoutShipping">
                    <LoadingOverlay loading={this.props.isUpdatingCustomer}>
                        {/* XNG-CHANGES: Adding CheckoutShippingFormContext */}
                        <CheckoutShippingFormContext.Provider value={this.state}>
                            <form id="checkoutShippingForm" onSubmit={e => this.handleFormSubmit(e)} noValidate>
                                <Zone zoneName="Content" contentId={this.props.id} />
                            </form>
                        </CheckoutShippingFormContext.Provider>
                    </LoadingOverlay>
                    {/* <Modal
                        {...styles.checkoutShippingPageModal}
                        isOpen={this.state.someCartLinesNotValidForWarehouse}
                        handleClose={this.modalCloseHandler}
                    >
                        <Notify type="danger">
                            {siteMessage(
                                "Guest_CartError_Some_CartLines_Not_Valid_For_Warehouse",
                                "Some of the items in your shopping cart are not available to be shipped to your postal code. Please remove these items to continue.",
                            )}
                        </Notify>
                    </Modal>
                    <Modal
                        {...styles.checkoutShippingPageModal}
                        isOpen={this.state.insufficientInventoryAtCheckout}
                        handleClose={this.modalCloseHandler}
                    >
                        <Notify type="danger">{siteMessage("Cart_InsufficientInventoryAtCheckout")}</Notify>
                    </Modal>
                    <Modals /> */}
                </Page>
            </StepperContextProvider>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutShippingPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "CheckoutShippingPage" instead of this constant.
 */
export const CheckoutShippingPageContext = "CheckoutShippingPage";
